import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { Rotate } from '@components/animators/Rotate';
import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { IconDropRight } from '@components/icons/dropright';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconTrash } from '@components/icons/trash';
import { hideModal, Modal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { formatNumber } from '@utils/numbers';

import { useJourneyActions } from 'api/journey-actions/hooks';
import { JourneyAction } from 'api/types';
import { collectBranchDescendants } from 'ui/authenticated/main/pages/journeys/journey-node-utils';
import { useJourneyDetailParams } from 'ui/hooks';
import { JourneyNode } from 'ui/journey-builder/node-types/types';

import { BranchActionCard } from './branch-action-card';

const AUTO_COLLAPSE_THRESHOLD = 24;
const LIST_ID = 'delete-branch-action-list';

export type DeleteBranchModalProps = {
  node: JourneyNode<'branch_base'>;
  onConfirm(): void;
};

export const DeleteBranchModal = ({ node, onConfirm }: DeleteBranchModalProps) => {
  const { t } = useScopedTranslate(locale);
  const { journeyId } = useJourneyDetailParams();
  const { data: actions, isLoading } = useJourneyActions(journeyId);
  const descendants = useMemo(() => {
    return collectBranchDescendants(actions ?? [], node.id);
  }, [actions, node.id]);

  const handleDelete = () => {
    onConfirm();
    hideModal();
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('deleteBranch')} icon={<IconFormIndicatorWarning />} />
      <DeleteBranchModalContent
        descendants={descendants}
        isLoading={isLoading || !actions}
        onDelete={handleDelete}
        onCancel={hideModal}
      />
    </Modal>
  );
};

export type DeleteBranchModalContentProps = {
  descendants: JourneyAction[];
  isLoading: boolean;
  onDelete(): void;
  onCancel(): void;
};

export const DeleteBranchModalContent = ({
  descendants,
  isLoading,
  onDelete,
  onCancel,
}: DeleteBranchModalContentProps) => {
  const { t } = useScopedTranslate(locale);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [expandOverride, setExpandOverride] = useState<boolean | null>(null);

  const count = descendants.length;
  const isExpanded = expandOverride ?? count <= AUTO_COLLAPSE_THRESHOLD;
  const isRecoverable =
    !isLoading && count > 0 && descendants.every((action) => action.hasPublishedSnapshot);
  const isDeleteDisabled = isLoading || (!isRecoverable && !hasConfirmed);

  return (
    <>
      <Modal.Content>
        <Stack space={6}>
          {!isLoading && (
            <Text
              text={isRecoverable ? t('recoverableInfo') : t('unrecoverableWarning')}
              color={isRecoverable ? 'textSecondary' : 'elementRed100'}
            />
          )}
          <Stack space={4}>
            <SectionHeader
              onClick={() => setExpandOverride(!isExpanded)}
              aria-expanded={isExpanded}
              aria-controls={LIST_ID}
            >
              <Stack orientation="horizontal" space={2} align="center">
                <IconTrash color="elementRed100" />
                <Text font="semibold14" text={t('actionsToRemove')} color="textSecondary" />
                <Badge
                  text={formatNumber(count)}
                  size="md"
                  bg="uiTrans5"
                  data-testid="delete-branch-count"
                />
              </Stack>
              <Rotate isRotated={isExpanded}>
                <IconDropRight size={16} color="teal200" />
              </Rotate>
            </SectionHeader>
            {isExpanded && (
              <Stack space={2} id={LIST_ID}>
                {descendants.map((action) => {
                  return (
                    <BranchActionCard
                      key={action.shortId}
                      action={{
                        action: action.action,
                        shortId: action.shortId,
                        description: action.description,
                      }}
                      isDeleteAction
                    />
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" space={4} justify="space-between" align="center">
          <div>
            {!isRecoverable && !isLoading && (
              <Checkbox label={t('iUnderstand')} value={hasConfirmed} onChange={setHasConfirmed} />
            )}
          </div>
          <Stack orientation="horizontal" space={4} align="center">
            <Button variant="secondary" onClick={onCancel} text={t('core.cancel')} />
            <Button
              variant="primary-red"
              iconLeft={<IconTrash />}
              text={t('core.delete')}
              disabled={isDeleteDisabled}
              title={isDeleteDisabled && !isLoading ? t('deleteDisabledHint') : undefined}
              onClick={onDelete}
              dataTestid="delete-branch-confirm"
            />
          </Stack>
        </Stack>
      </Modal.Footer>
    </>
  );
};

const SectionHeader = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
});

const locale = {
  en: {
    deleteBranch: 'Delete branch',
    actionsToRemove: 'Actions that will be removed',
    recoverableInfo:
      'These actions were previously published so you can restore them by reverting to an earlier version',
    unrecoverableWarning: "This branch was never published, so it can't be recovered",
    iUnderstand: 'I understand',
    deleteDisabledHint: 'Tick "I understand" to enable deletion',
  },
};
